import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SignedIn, SignedOut, SignIn, SignUp, useAuth, useOrganizationList } from '@clerk/clerk-react';

const CONVEX_URL = process.env.REACT_APP_CONVEX_URL;
const rawConvexHttpUrl =
  process.env.REACT_APP_CONVEX_HTTP_URL ||
  (CONVEX_URL ? CONVEX_URL.replace('.convex.cloud', '.convex.site') : '');
const CONVEX_HTTP_URL = (() => {
  if (!rawConvexHttpUrl) {
    return '';
  }
  try {
    return new URL(rawConvexHttpUrl).origin;
  } catch (error) {
    console.error('Invalid Convex HTTP URL:', error);
    return '';
  }
})();

const JoinOrganizationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn, sessionId } = useAuth();
  const { setActive } = useOrganizationList();
  const [orgName, setOrgName] = useState('');
  const [isValidating, setIsValidating] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [didJoin, setDidJoin] = useState(false);
  const [error, setError] = useState('');

  const token = searchParams.get('joinToken') || '';
  const redirectUrl = useMemo(() => {
    const query = searchParams.toString();
    return query ? `/join?${query}` : '/join';
  }, [searchParams]);

  useEffect(() => {
    const validate = async () => {
      if (!CONVEX_HTTP_URL) {
        setError('Missing Convex configuration.');
        setIsValidating(false);
        return;
      }
      if (!token) {
        setError('Missing invite code.');
        setIsValidating(false);
        return;
      }

      try {
        const response = await fetch(`${CONVEX_HTTP_URL}/org/join/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.error || 'Invalid or expired invite code.');
        }

        const body = await response.json();
        setOrgName(body?.orgName || 'Family');
      } catch (err) {
        const message = err?.message || 'Unable to validate invite code.';
        setError(message);
      } finally {
        setIsValidating(false);
      }
    };

    validate();
  }, [token]);

  useEffect(() => {
    const join = async () => {
      if (!isSignedIn || !sessionId || !token || isJoining || error || isValidating) {
        return;
      }

      setIsJoining(true);
      try {
        const response = await fetch(`${CONVEX_HTTP_URL}/org/join/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, sessionId }),
        });

        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body?.error || 'Unable to join family.');
        }

        if (body?.orgId) {
          try {
            await setActive({ organization: body.orgId });
          } catch (setActiveError) {
            console.warn('Failed to activate organization:', setActiveError);
          }
        }
        setDidJoin(true);
        setTimeout(() => {
          navigate('/app', { replace: true });
        }, 1200);
      } catch (err) {
        const message = err?.message || 'Unable to join family.';
        setError(message);
      } finally {
        setIsJoining(false);
      }
    };

    if (!CONVEX_HTTP_URL) return;
    join();
  }, [error, isJoining, isSignedIn, isValidating, navigate, sessionId, token, setActive]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
      <div className="max-w-md w-full space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900">
          Join {orgName || 'a Saverly family'}
        </h1>
        {isValidating && <p className="text-gray-600">Validating invite…</p>}
        {!isValidating && error && (
          <p className="text-red-600 text-sm">{error}</p>
        )}

        {!isValidating && !error && (
          <>
            <SignedOut>
              <p className="text-gray-600">
                Sign in or create an account to join {orgName || 'this family'}.
              </p>
              <div className="grid gap-4">
                <SignIn redirectUrl={redirectUrl} />
                <SignUp redirectUrl={redirectUrl} />
              </div>
            </SignedOut>

            <SignedIn>
              <p className="text-gray-600">
                {didJoin
                  ? 'Joined successfully! Opening the app…'
                  : isJoining
                    ? 'Joining family…'
                    : 'Finishing your join…'}
              </p>
            </SignedIn>
          </>
        )}
      </div>
    </div>
  );
};

export default JoinOrganizationPage;
